import { TooltipContainer } from "@outplayed/tooltips";
import classNames from "classnames";
import SubtitleAndDescription from "components/Common/SubtitleAndDescription";
import { MediaQuery } from "@outplayed/responsive";
import { Abilities } from "./BuildPageContainer";
import { getDeadlockImagePath } from "lib/general-helper";
import UnlockOrder from "./UnlockOrder";
import AbilityTooltip from "components/AbilityTooltip";
import UpgradeTooltip from "components/UpgradeTooltip";

interface SkillPathProps {
  abilitiesBySlot: Abilities;
  abilitiesByPickOrder: { abilityName: string }[];
}

const SkillPath = (props: SkillPathProps) => {
  const { abilitiesBySlot, abilitiesByPickOrder } = props;

  // create a 4x12 array filled with 0's - this maps out the shape of the skill path
  const pointsMap = Array.from({ length: 4 }, () => Array(12).fill(0));

  // we need to track the number of times an ability appears because the first time is
  // just for unlock, then the future times are for points 1, 2, and 5 respectively
  const appearanceCount: { [key: string]: number } = {};

  // since there are more abilities than columns, we want to start at -1 and only
  // increment the column if this is not the first time we're seeing the ability
  let column = -1;
  abilitiesByPickOrder.forEach((ability) => {
    // get the row based on finding the matching name in abilitiesBySlot
    const row = Object.values(abilitiesBySlot).findIndex((item) => item.name === ability.abilityName);

    if (row === -1) return;

    if (!appearanceCount[ability.abilityName]) {
      appearanceCount[ability.abilityName] = 1;
    } else {
      appearanceCount[ability.abilityName]++;
      column++;
    }

    if (appearanceCount[ability.abilityName] === 2) pointsMap[row][column] = 1;
    if (appearanceCount[ability.abilityName] === 3) pointsMap[row][column] = 2;
    if (appearanceCount[ability.abilityName] === 4) pointsMap[row][column] = 5;
  });

  return (
    <div className="unlock-order-and-skill-path-wrapper">
      <MediaQuery min="TABLET" max="DESKTOP_LARGE" renderNullOnFail>
        <div className="unlock-order-container">
          <SubtitleAndDescription subtitle="Unlock Order" />
          <UnlockOrder abilitiesByPickOrder={abilitiesByPickOrder} abilitiesBySlot={abilitiesBySlot} />
        </div>
      </MediaQuery>
      <div className="skill-path-wrapper">
        <div className="skill-path-header">
          <MediaQuery min="MOBILE_SMALL" max="TABLET" renderNullOnFail>
            <SubtitleAndDescription subtitle="Skill Path" />
          </MediaQuery>
          <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail>
            <SubtitleAndDescription subtitle="Skill Path" description="Most popular skill leveling order" />
          </MediaQuery>
          <MediaQuery min="MOBILE_SMALL" max="MOBILE_LARGE" renderNullOnFail>
            <div className="unlock-order-container absolute-position">
              <div className="unlock-order-label">Unlock Order:</div>{" "}
              <UnlockOrder abilitiesByPickOrder={abilitiesByPickOrder} abilitiesBySlot={abilitiesBySlot} />
            </div>
          </MediaQuery>
        </div>
        <div className="main-content-wrapper">
          <div className="left-side">
            {Object.keys(abilitiesBySlot).map((key, index) => {
              const ability = abilitiesBySlot[key];
              const abilityImage = getDeadlockImagePath(abilitiesBySlot[key].abilityImage, abilitiesBySlot[key].name);

              return (
                <div className="skill-icon-and-name-wrapper" key={index}>
                  <TooltipContainer
                    tooltipInfo={
                      <AbilityTooltip
                        ability={{
                          name: ability.name,
                          description: ability.desc,
                          abilityImage: ability.abilityImage,
                          stats: ability.stats,
                          t1Desc: ability.t1Desc,
                          t2Desc: ability.t2Desc,
                          t3Desc: ability.t3Desc,
                          upgrades: ability.upgrades,
                        }}
                      />
                    }
                  >
                    <div className="skill-square">
                      <img
                        src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${abilityImage}.png`}
                        alt={abilitiesBySlot[key].name}
                      />
                      <div className="skill-number">{index + 1}</div>
                    </div>
                  </TooltipContainer>
                  <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail>
                    <div className="skill-name">{abilitiesBySlot[key].name}</div>
                  </MediaQuery>
                  <MediaQuery min="MOBILE_LARGE" max="TABLET" renderNullOnFail>
                    <div className="skill-name">{abilitiesBySlot[key].name}</div>
                  </MediaQuery>
                </div>
              );
            })}
          </div>
          <div className="right-side">
            {pointsMap.map((row, rowIndex) => {
              // Get the correct ability key for this row
              const abilityKey = Object.keys(abilitiesBySlot)[rowIndex];
              const ability = abilitiesBySlot[abilityKey];

              return (
                <div className="points-row" key={rowIndex}>
                  {row.map((square, squareIndex) => {
                    if (square === 0) {
                      return <div className="square-container" key={squareIndex}></div>;
                    }

                    // Determine which tier we are dealing with
                    let tier: 1 | 2 | 3;
                    if (square === 1) {
                      tier = 1;
                    } else if (square === 2) {
                      tier = 2;
                    } else {
                      // square === 5 means tier 3
                      tier = 3;
                    }

                    return (
                      <TooltipContainer
                        tooltipInfo={
                          <UpgradeTooltip
                            tier={tier}
                            t1Desc={ability.t1Desc}
                            t2Desc={ability.t2Desc}
                            t3Desc={ability.t3Desc}
                            upgrades={ability.upgrades}
                          />
                        }
                        key={squareIndex}
                      >
                        <div
                          className={classNames("square-container", {
                            "one-or-two-points": square === 1 || square === 2,
                            "five-points": square === 5,
                          })}
                        >
                          {square > 0 && square}
                        </div>
                      </TooltipContainer>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillPath;
